import {
  KnownProjectDtoProjectType,
  KnownProjectDtoStatus,
  ProjectDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { RJSFSchema } from '@rjsf/utils';
import { convertHyphenated } from '../../../../core/util/string-conversion';
import { rawTimeZones } from '@vvo/tzdb';
import { DAYS } from './cron-converter';
import { ProjectSettingsFormOutput } from './form-output';
import { JSONSchema7 } from 'json-schema';
import { formBooleanToOneOf } from '../shared/form-schema.utils';

export type FormOneOf<T> = {
  type: 'string';
  title: string;
  enum: [T];
};

/** Carrier and region projects hold couriers, not buildings, so most project settings do not apply. */
export type ProjectSettingsFormVariant = 'standard' | 'carrier' | 'region';

/** Only ever created by the Add carrier wizard, never by the standard form. */
const CARRIER_PROJECT_TYPES: string[] = [KnownProjectDtoProjectType.Carrier, KnownProjectDtoProjectType.Region];

/** The subset each carrier-side project exposes. A region has no logo of its own. */
export const CARRIER_VARIANT_FIELDS: Record<Exclude<ProjectSettingsFormVariant, 'standard'>, readonly string[]> = {
  carrier: ['name', 'type', 'status', 'parent', 'shortcode', 'image'],
  region: ['name', 'type', 'status', 'parent', 'shortcode']
};

const CARRIER_VARIANT_REQUIRED: Record<Exclude<ProjectSettingsFormVariant, 'standard'>, string[]> = {
  carrier: ['name', 'shortcode'],
  region: ['name']
};

export function projectSettingsFormVariant(projectType?: string): ProjectSettingsFormVariant {
  switch (projectType) {
    case KnownProjectDtoProjectType.Carrier:
      return 'carrier';
    case KnownProjectDtoProjectType.Region:
      return 'region';
    default:
      return 'standard';
  }
}

export class ProjectSettingsFormSchema {
  static FromProjectData(
    formOutput: ProjectSettingsFormOutput,
    parentProjectOptions: ProjectDto[],
    readOnly: boolean = false,
    showInventoryBookingFlowControl: boolean = false,
    variant: ProjectSettingsFormVariant = 'standard'
  ): RJSFSchema {
    const isCarrier = variant !== 'standard';
    // The report it gates is downloaded from the Orders screen.
    const allowPersonalInfoControl = formOutput.showOrders === 'yes';

    const parentProjectOption = parentProjectOptions.find((p) => p.id === formOutput.parent);
    const canEditParentProject = parentProjectOption !== undefined;

    const parentProjectInputOptions: { id: string; name: string }[] = [...parentProjectOptions];
    if (formOutput.parent && !parentProjectOption) {
      parentProjectInputOptions.push({
        id: formOutput.parent,
        name: 'Access Restricted'
      });
    }

    const schema: RJSFSchema = {
      properties: {
        name: {
          type: 'string',
          title: 'Project name'
        },
        type: {
          type: 'string',
          title: 'Type',
          // Changing it would orphan the organisation, or skip the wizard's second step.
          readOnly: isCarrier,
          // Setting an empty string as the default value prevents the form library from including an empty select option
          default: '',
          oneOf: Object.values(KnownProjectDtoProjectType)
            .filter((type) => CARRIER_PROJECT_TYPES.includes(type) === isCarrier)
            .map((type) => ({
              type: 'string',
              title: convertHyphenated(type, { capitalizeEachWord: true }),
              enum: [type]
            }))
        },
        // Shown, not edited: the edit endpoint refuses 'deleted', and Delete project is an action.
        ...(isCarrier && {
          status: {
            type: 'string',
            title: 'Status',
            readOnly: true,
            default: '',
            oneOf: Object.values(KnownProjectDtoStatus).map((status) => ({
              type: 'string',
              title: convertHyphenated(status, { capitalizeEachWord: true }),
              enum: [status]
            }))
          }
        }),
        parent: {
          type: 'string',
          title: 'Parent',
          // A region stays under the carrier that made it.
          readOnly: variant === 'region' || !canEditParentProject,
          ...(parentProjectInputOptions.length > 0 && {
            oneOf: parentProjectInputOptions.map((p) => ({
              type: 'string',
              title: p.name,
              enum: [p.id]
            }))
          })
        },
        shortcode: {
          type: 'string',
          title: 'URL short name',
          pattern: '^[a-zA-Z0-9_&-]*$'
        },
        senderID: {
          type: 'string',
          title: 'Sender ID'
        },
        image: {
          type: 'string'
        },
        addressLine1: {
          type: 'string',
          title: 'Address line 1'
        },
        addressLine2: {
          type: 'string',
          title: 'Address line 2'
        },
        suburb: {
          type: 'string',
          title: 'Suburb'
        },
        postcode: {
          type: 'string',
          title: 'Postcode'
        },
        state: {
          type: 'string',
          title: 'State'
        },
        country: {
          type: 'string',
          title: 'Country',
          default: '',
          oneOf: [
            {
              type: 'string',
              title: 'None',
              enum: ['']
            },
            {
              type: 'string',
              title: 'Australia',
              enum: ['Australia']
            },
            {
              type: 'string',
              title: 'New Zealand',
              enum: ['New Zealand']
            }
          ]
        },
        timezone: {
          type: 'string',
          title: 'Timezone',
          readOnly: !formOutput.country,
          default: '',
          ...(ProjectSettingsFormSchema.IsValidCountry(formOutput.country) && {
            oneOf: ProjectSettingsFormSchema.GetTimeZones(formOutput.country).map((tz) => ({
              type: 'string',
              title: tz.rawFormat,
              enum: [tz.name]
            }))
          })
        },
        isManagedByAc: {
          type: 'boolean',
          title: 'Managed by AC',
          default: false
        },
        showDeliveries: {
          type: 'string',
          title: 'Show deliveries',
          default: '',
          oneOf: formBooleanToOneOf()
        },
        showOrders: {
          type: 'string',
          title: 'Show orders',
          default: '',
          oneOf: formBooleanToOneOf()
        },
        ...(allowPersonalInfoControl && {
          allowPersonalInfo: {
            type: 'string',
            title: 'Allow personal information in reports',
            description: 'Only team members with permission to export personal data will see it.',
            default: 'no',
            oneOf: formBooleanToOneOf()
          }
        }),
        ...(showInventoryBookingFlowControl && {
          enableInventoryBookingFlow: {
            type: 'string',
            title: 'Enable inventory booking flow',
            default: 'no',
            oneOf: formBooleanToOneOf()
          }
        }),
        overduePeriod: {
          type: 'number',
          title: 'Overdue after X hours'
        },
        reminderPeriod: {
          type: 'number',
          title: 'Prevent reminder within X hours'
        },
        ...DAYS.reduce(
          (acc, day) => ({
            ...acc,
            [day]: {
              type: 'string',
              title: `${day.charAt(0).toUpperCase()}${day.slice(1)}`,
              format: 'time'
            }
          }),
          {}
        )
      },
      // The parent project is required for all projects except the root project.
      // When a non-root-project is being created or edited, it is not possible to
      // clear the parent field, so its simpler to not make it required.
      required: ['name', 'type', 'shortcode', 'showDeliveries', 'showOrders']
    };
    if (variant !== 'standard') {
      const fields = CARRIER_VARIANT_FIELDS[variant];
      schema.properties = Object.fromEntries(
        Object.entries(schema.properties ?? {}).filter(([key]) => fields.includes(key))
      );
      schema.required = CARRIER_VARIANT_REQUIRED[variant];
    }
    if (readOnly && schema.properties) {
      // Mark all fields as readOnly
      Object.entries(schema.properties).forEach(([_, value]) => {
        (value as JSONSchema7).readOnly = true;
      });
    }

    return readOnly ? { ...schema, readOnly: true } : schema;
  }

  private static GetTimeZones(country?: string) {
    if (!country) {
      return [];
    }
    const countryCode = ProjectSettingsFormSchema.MapCountryToCountryCode(country);
    if (!countryCode) {
      return [];
    }
    return rawTimeZones.filter((tz) => tz.countryCode === countryCode);
  }

  private static MapCountryToCountryCode(country: string): string {
    switch (country) {
      case 'Australia':
        return 'AU';
      case 'New Zealand':
        return 'NZ';
      default:
        return '';
    }
  }

  private static IsValidCountry(country?: string): boolean {
    return !!country && ['Australia', 'New Zealand'].includes(country);
  }
}
